
// components/SystmOneActionChips.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Tooltip,
  InputLabel,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import moment from "moment";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import css from "./index.module.scss";
import Modal from "../../../components/atoms/Modal";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers";
import constant from "../../../constants/constant";
import { addSystemDate, getCaseloadList } from "../../../redux/action";

interface ChipData {
  label: string;
  date: string;
  key: string;
  path: string;
}

interface Props {
  item: any;
  param: string;
  page: any;
  limit: any;
  isfilterObject:any
}

const SystmOneActionChips: React.FC<Props> = ({ item, param, page, limit,isfilterObject }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [refType, setRefType] = useState("");
  const [formData, setFormData] = useState({ date: "", time: "" });
  const [updateAll, setUpdateAll] = useState(false);
  const [redChipKeys, setRedChipKeys] = useState<string[]>([]);

  const chipData: ChipData[] = [
    {
      label: "Referral Uploaded",
      date: item?.system_one_referral_date,
      key: "system_one_referral_date",
      path: item.pathway_status,
    },
    {
      label: "Education Reports Uploaded",
      date: item?.system_one_education_report_date,
      key: "system_one_education_report_date",
      path: item.pathway_status,
    },
    {
      label: "Parent Reports Uploaded",
      date: item?.system_one_parent_report_date,
      key: "system_one_parent_report_date",
      path: item.pathway_status,
    },
    ...(param === "closed"
      ? [
          {
            label: "Outcome Letter Uploaded",
            date: item?.system_one_outcome_date,
            key: "system_one_outcome_date",
            path: item.pathway_status,
          },
        ]
      : []),
  ];

  const handleChipClick = (key: string) => {
    const redKeys: string[] = chipData
      .filter(
        ({ date, key, path }) =>
          !date &&
          ((path.includes("1") && key === "system_one_referral_date") ||
            (path.includes("2") &&
              key === "system_one_education_report_date") ||
            (path.includes("3") && key === "system_one_parent_report_date") ||
            (path.includes("6") && key === "system_one_outcome_date"))
      )
      .map(({ key }) => key);

    setRefType(key);
    setRedChipKeys(redKeys);
    setIsOpen(true);
  };

  const handleOnChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const combined = `${formData.date} ${formData.time}`;
    const isoString = moment
      .utc(combined, "DD/MM/YYYY HH:mm")
      .format("YYYY-MM-DDTHH:mm:ssZ");

    const keysToUpdate = updateAll ? redChipKeys : [refType];

    const payload = keysToUpdate.reduce((acc, key) => {
      acc[key] = isoString;
      return acc;
    }, {} as Record<string, string>);

    dispatch(addSystemDate(item.id, payload));

    setTimeout(() => {
      dispatch(
        getCaseloadList({
          page,
          limit,
          search: "",
          status:
            param !== "closed"
              ? constant.ACTIVE_REFERRAL_STATUS.ACCEPTED
              : constant.ACTIVE_REFERRAL_STATUS.CLOSED,
          filter: isfilterObject,
        })
      );
    }, 1000);

    setIsOpen(false);
    setFormData({ date: "", time: "" });
    setUpdateAll(false); // reset checkbox
  };

  const isFormValid = formData.date !== "" && formData.time !== "";

  useEffect(() => {
    const currentDate = dayjs();
    const formattedDate = currentDate.format("DD/MM/YYYY");
    const formattedTime = currentDate.format("HH:mm");

    setFormData((prevFormData) => ({
      ...prevFormData,
      date: formattedDate,
      time: formattedTime,
    }));
    // eslint-disable-next-line
  }, [isOpen]);

  const redChipsCount = chipData.filter(
    ({ date, key, path }) =>
      !date &&
      ((path.includes("1") && key === "system_one_referral_date") ||
        (path.includes("2") && key === "system_one_education_report_date") ||
        (path.includes("3") && key === "system_one_parent_report_date") ||
        (path.includes("6") && key === "system_one_outcome_date"))
  ).length;

  return (
    <>
      <Box className={css.chipsStyle}>
        {chipData.map(({ label, date, key, path }) => {
          let chipColor = "gray";
          let clickable = false;

          if (date) {
            chipColor = "green";
          } else if (
            (path.includes("1") && key === "system_one_referral_date") ||
            (path.includes("2") &&
              key === "system_one_education_report_date") ||
            (path.includes("3") && key === "system_one_parent_report_date") ||
            (path.includes("6") && key === "system_one_outcome_date")
          ) {
            chipColor = "red";
            clickable = true;
          }

          return (
            <Tooltip
              key={key}
              title={
                date
                  ? moment(date).utc().format("DD/MM/YYYY : HH:mm")
                  : "No date available"
              }
            >
              <Chip
                label={label}
                size="small"
                sx={{
                  color: chipColor,
                  "& .MuiChip-label": {
                    padding: 0,
                  },
                }}
                onClick={() => clickable && handleChipClick(key)}
              />
            </Tooltip>
          );
        })}
      </Box>

      <Modal
        open={isOpen}
        handleClose={() => setIsOpen(false)}
        title={"Add SystmOne Date/Time"}
      >
        <Box className={css.clinicModal}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} py={4}>
              <Grid item sx={{ width: "50%" }}>
                <InputLabel
                  htmlFor="outlined-adornment-password"
                  sx={{
                    textAlign: "left",
                    fontFamily: "poppins",
                    marginBottom: "0.5rem",
                  }}
                >
                  Select Date
                </InputLabel>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      formData.date ? dayjs(formData.date, "DD/MM/YYYY") : null
                    }
                    onChange={(newValue) => {
                      if (newValue) {
                        handleOnChange({
                          target: {
                            name: "date",
                            value: newValue.format("DD/MM/YYYY"),
                          },
                        });
                      }
                    }}
                    format="DD/MM/YYYY"
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item sx={{ width: "50%" }}>
                <InputLabel
                  htmlFor="outlined-adornment-password"
                  sx={{
                    textAlign: "left",
                    fontFamily: "poppins",
                    marginBottom: "0.5rem",
                  }}
                >
                  Select Time
                </InputLabel>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    value={
                      formData.time && dayjs(formData.time, "HH:mm").isValid()
                        ? dayjs(formData.time, "HH:mm")
                        : null
                    }
                    onChange={(newValue) => {
                      if (newValue && newValue.isValid()) {
                        handleOnChange({
                          target: {
                            name: "time",
                            value: newValue.format("HH:mm"),
                          },
                        });
                      }
                    }}
                    sx={{ width: "100%" }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            {redChipsCount >= 3 && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateAll}
                      onChange={() => setUpdateAll((prev) => !prev)}
                      size="small"
                    />
                  }
                  label="Update All Reports"
                  sx={{ alignItems: "center", gap: "2px" }}
                />
              </Grid>
            )}

            <Grid
              className={css.btnWrapper}
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                alignItems: "center",
                mt: "15px",
              }}
            >
              <Button
                sx={{ width: "30%" }}
                variant={"contained"}
                disabled={!isFormValid}
                type="submit"
              >
                Submit
              </Button>
            </Grid>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default SystmOneActionChips;